import BaseService from './base-service';

// Utils
import { deepExtend, deepExtendConcat } from 'libs/utils/objects';
import {
    applyPermissions,
    controllerToRoute,
    getAllExtensions,
    remapTemplatesAndControllers,
    sanitizeNames
} from 'libs/utils/blueprint';
import { get, isEmpty } from 'lodash';

// Constants
import { API_BASE_PATH } from 'libs/utils/constants';

/**
 * Blacklisted modules
 * These are the modules of packages that we merged back to BSTG
 * Files from these modules won't be loaded
 * @constant {String[]} MODULES_BLACKLIST
 */
const MODULES_BLACKLIST = ['modal-help', 'metadata_filter', 'metadata_selector', 'theme-editor', 'files'];

/**
 * Blueprint document API endpoint. Interpolations: `{{eventId}}`.
 * @constant {String} BLUEPRINT_DOCUMENT_ENDPOINT
 */
export const BLUEPRINT_DOCUMENT_ENDPOINT = `${API_BASE_PATH}/events/{{eventId}}/docbyid/blueprint`;

/**
 * Compile blueprint API endpoint. Interpolations: `{{eventId}}`.
 * @constant {String} COMPILE_BLUEPRINT_ENDPOINT
 */
export const COMPILE_BLUEPRINT_ENDPOINT = `${API_BASE_PATH}/eid/{{eventId}}/compile/blueprint`;

/**
 * @constant {String} FP_TYPE_LISTING_PATH FP Type specific backstage path. Interpolations: `{{eventId}}`, `{{fpType}}`.
 * @private
 */
const FP_TYPE_LISTING_PATH = '/event/{{eventId}}/{{fpType}}s';

// Default blueprint object
import DEFAULT_ROUTES from './default-routes';
import { getRouteHref } from 'libs/utils/modules';

/**
 * Provides utils for blueprint modules.
 *
 * @example
 * import BlueprintService from 'libs/services/blueprint';
 * ...
 * const blueprint = new BlueprintService();
 */
export default class BlueprintService extends BaseService {

    /**
     * Creates a new BlueprintService instance.
     */
    constructor() {
        super();
        this.blueprint = {};
    }

    /**
     * Initializes service with given blueprint
     *
     * @param {Object} options additional options
     * @param {Object} options.user the user object
     * @param {Function} options.require the require function for global sources
     * @param {Object} [options.event] the event object
     *
     * @returns {Object} the blueprint object
     */
    async init(options) {
        this.addBlueprint(DEFAULT_ROUTES, options);

        if (options.event) {
            await this.loadBlueprint(options);
        }

        return this.blueprint;
    }

    /**
     * Compiles a blueprint for the specified event.
     *
     * @param {string} eventId - The ID of the event.
     *
     * @returns {Promise} - A promise that resolves with the compiled blueprint.
     */
    async compile(eventId) {
        try {
            const compileUrl = COMPILE_BLUEPRINT_ENDPOINT.replace('{{eventId}}', eventId);
            return await this.post(compileUrl, {});
        } catch (error) {
            console.error(`[BlueprintService] Failed to compile blueprint for event ${eventId}`, error.message);
            console.error('[BlueprintService] The resulting blueprint may be incomplete and not reflecting latest changes.');
        }
    }

    /**
     * Fetches blueprint for given event and stores it in the service
     *
     * @param {Object} options additional options
     * @param {Object} options.event the event object
     * @param {Function} options.require the require function for global sources
     * @param {Object} [options.user] the user object
    *
     * @returns {Promise<Object>} blueprint
     */
    async loadBlueprint(options) {
        const eventId = options.event._id || options.event.id;
        await this.compile(eventId);
        const url = BLUEPRINT_DOCUMENT_ENDPOINT.replace('{{eventId}}', eventId);
        const { data: blueprint } = await this.get(url);

        if (!blueprint) {
            throw new Error(`Missing blueprint for event ${eventId}`);
        }

        this.addBlueprint(blueprint, options);
        await this.loadGlobalSources(options.require);

        return this.blueprint;
    }

    /**
     * Loads the global sources for the blueprint.
     *
     * @param {Function} require - The require function to use for loading the global sources.
     *
     * @returns {Promise} A promise that resolves when all global sources have been loaded.
     */
    async loadGlobalSources(require) {
        return Promise.all(this.getGlobalSourceUrls().map(url => require(url)));
    }

    /**
     * Retrieves the global source URLs from the blueprint modules.
     *
     * This method iterates over the modules in the blueprint and collects source URLs
     * from modules that are not blacklisted, have no controllers, and have a source URL.
     * The collected URLs are filtered to exclude any URLs that are blacklisted.
     *
     * @returns {Array} An array of global base dependency URLs.
     */
    getGlobalSourceUrls() {
        const globalBaseDependencies = [];

        for (const mod of Object.values(this.blueprint.modules)) {
            // in a case where we have a module with a sourceUrl
            // but no controllers, add the code urls to base deps
            const sourceUrl = mod.sourceUrl || [];

            if (!MODULES_BLACKLIST.includes(mod.name) && isEmpty(mod.controllers) && sourceUrl.length) {
                const urls = Array.isArray(sourceUrl) ? sourceUrl : [sourceUrl];
                globalBaseDependencies.push(...urls);
            }
        }

        return globalBaseDependencies;
    }

    /**
     * Get the blueprint modules for the current event,
     * where restricted modules and controllers have been removed
     *
     * @param {String} [type] if specified, returns modules of the given type
     * @returns {Object}
     */
    getModules(type) {
        const modules = this.blueprint.modules;
        const filtered = {};

        for (const [moduleKey, moduleDescriptor = {}] of Object.entries(modules)) {
            // if module have no type, it belongs the the event main navigation type
            const moduleType = moduleDescriptor.type || 'event';

            // filter according to type
            if (modules[moduleKey] && (!type || moduleType === type)) {
                filtered[moduleKey] = moduleDescriptor;
            }
        }

        return filtered;
    }

    /**
     * Retrieve the module given its name
     *
     * @param {String} name the name of the module
     * @returns {Object|undefined} the module or undefined
     */
    getModuleByName(name) {
        return (this.blueprint?.modules || {})[name];
    }

    /**
     * Checks if the given module is installed in the current event/
     *
     * @param {String} name the name of the module to check
     *
     * @returns {boolean} true if the module is installed
     */
    isModuleInstalled(name) {
        return Boolean(this.getModuleByName(name));
    }

    /**
     * Retrieve a controller from a module given its name
     *
     * @param {String} name the name of the module
     * @param {String} controller the name of the controller
     * @returns {Object|undefined} the controller or undefined
     */
    getControllerByName(name, controller) {
        const module = (this.blueprint.modules || {})[name] || {};
        return get(module, ['controllers', controller]);
    }

    /**
     * Compares given array of modules with the one from current blueprint and
     * returns new controllers with `showOnInstall` flag.
     *
     * @param {Array<Object>} modules modules from blueprint
     * @returns {Array<Object>} new `showOnInstall` controllers
     */
    getShowOnInstallCtrls(modules) {
        const currentModules = this.getModules();
        const result = [];

        for (const moduleKey of Object.keys(currentModules)) {
            const module = currentModules[moduleKey];
            const showOnInstallCtrls = Object.values(module.controllers || {}).filter(ctrl => ctrl.showOnInstall);

            if (!showOnInstallCtrls.length) {
                continue;
            }

            const prevModule = modules[moduleKey] || {};

            for (const ctrl of showOnInstallCtrls) {
                const prevModuleCtrls = Object.values(prevModule.controllers || {});
                const prevCtrl = prevModuleCtrls.find(c => c.showOnInstall && c.controller === ctrl.controller);

                if (!prevCtrl) {
                    result.push(ctrl);
                }
            }
        }

        return result;
    }

    /**
     * Given an FP type and and Event ID, this method returns the path of
     * the page where we list that type of documents.
     *
     * @param {String} eventId the ID of the event
     * @param {String} fpType the desired FP type
     *
     * @return {String} the path of the page where we list the given FP type
     */
    getFpTypeListingPath(eventId, fpType) {
        let listRoute = this.getFpTypeHref(eventId, fpType, 'list');
        if (!listRoute) {
            // Legacy support
            listRoute = this.buildUrl(FP_TYPE_LISTING_PATH, { eventId, fpType });
        }
        return listRoute;
    }

    /**
     * Retrieves the view path for a specific fpType and eventId.
     *
     * @param {string} eventId - The ID of the event.
     * @param {string} fpType - The type of the fp (e.g., 'type1', 'type2').
     * @param {object} [doc={}] - The document to link to.
     * @param {boolean} [partialAllowed=false] - Whether partial links are allowed.
     *
     * @returns {string|null} The view path for the specified fpType and eventId, or null if the module is not found.
     */
    getFpTypeViewPath(eventId, fpType, doc = {}, partialAllowed = false) {
        return this.getFpTypeHref(eventId, fpType, 'view', doc, partialAllowed);
    }

    /**
     * Generates a URL (href) for a specific fingerprint type and event.
     *
     * @param {string} eventId - The ID of the event.
     * @param {string} fpType - The fingerprint type.
     * @param {'list'|'create'|'view'|'import'|'custom'|'stat'} type - The type of the controller.
     * @param {Object} [doc={}] - Additional document parameters to include in the URL.
     * @param {boolean} [partialAllowed=false] - Whether partial URLs (containing '{{') are allowed.
     * @returns {string|null} The generated URL or null if the controller is not found or partial URLs are not allowed.
     */
    getFpTypeHref(eventId, fpType, type, doc = {}, partialAllowed = false) {
        const route = this.getFpTypeRoute(fpType, type);
        if (!route) {
            return null;
        }

        const { meta: { module, controllerName } } = route;
        const href = getRouteHref(module, controllerName, { eventId, ...doc });
        return !partialAllowed && href.includes('{{') ? null : href;
    }

    /**
     * Retrieves the requested controller of a specific fp type.
     *
     * @param {string} fpType - The fp type to get controllers for.
     * @param {'list'|'create'|'view'|'import'|'custom'|'stat'} type - The type of the controller.
     *
     * @returns {Object|undefined} The controller object if found, otherwise undefined.
     */
    getFpTypeRoute(fpType, type) {
        return this.getRoutesForFpType(fpType).find(ctrl => ctrl.meta.type === type);
    }

    /**
     * Retrieves controllers for a given fpType from the blueprint modules.
     *
     * @param {string} fpType - The fpType to filter controllers by.
     *
     * @returns {{ moduleName: string, type: string, controllerName: string, create?: Object, list?: Object, view?: Object, stat?: Object, custom?: Object, import?: Object }[]} An array of objects representing the controllers that match the given fpType.
     */
    getRoutesForFpType(fpType) {
        if (!fpType) {
            return [];
        }
        const routes = [];
        for (const module of Object.values(this.blueprint.modules)) {
            if (!module.controllers) {
                continue;
            }

            for (const [name, controller] of Object.entries(module.controllers)) {
                const resolverFpType = controller.resolvers?.default?.options?.fpType;
                const creationFpType = controller.routeOptions?.modelFpType;
                const routeFpType = controller.routeOptions?.crudOpts?.fpType;
                const importFpType = controller.routeOptions?.dataImportFpType;
                const statFpType = controller.routeOptions?.fpType;

                if (![resolverFpType, creationFpType, routeFpType, importFpType, statFpType].includes(fpType)) {
                    continue;
                }

                let type = 'custom';

                if (creationFpType) {
                    type = 'create';
                } else if (controller.controller === 'GenericViewItemCtrl' || controller.componentName === 'GenericForm') {
                    type = 'view';
                } else if (controller.componentName === 'GenericList' || controller.type === 'list') {
                    type = 'list';
                } else if (importFpType) {
                    type = 'import';
                } else if (statFpType) {
                    type = 'stat';
                }

                const route = controllerToRoute(module, name, { type });
                if (route) {
                    routes.push(route);
                }
            }
        }

        return routes;
    }

    /**
     * Adds a blueprint to the existing blueprint after applying permissions, sanitizing names,
     * and remapping templates and controllers.
     *
     * @param {Object} blueprint - The blueprint object to be added.
     * @param {Object} [options] - Additional options to apply to the blueprint.
     * @param {Object} [options.event] - The event object.
     * @param {Object} [options.user] - The user object.
     *
     * @private
     */
    addBlueprint(blueprint, options) {
        // Sanitize and remap templates and controllers
        sanitizeNames(blueprint.modules);
        applyPermissions(blueprint, options);
        remapTemplatesAndControllers(blueprint);

        deepExtend(this.blueprint, blueprint);

        this.extendModules();
    }

    /**
     * Extends the modules in the blueprint with additional extensions.
     *
     * This method retrieves all extensions for the blueprint's modules and attempts to extend each module with its corresponding extension.
     * If a module does not exist in the blueprint, a warning is logged and the extension is skipped.
     *
     * @method extendModules
     * @memberof BlueprintService
     *
     * @private
     */
    extendModules() {
        const extensions = getAllExtensions(this.blueprint.modules);

        for (const modules of extensions) {
            for (const [moduleName, extension] of Object.entries(modules)) {
                if (['order'].includes(moduleName)) {
                    // Skip reserved keywords
                    continue;
                }

                if (!this.blueprint.modules[moduleName]) {
                    console.warn('[BlueprintService] Trying to extend a module that is not in the blueprint:', moduleName);
                    continue;
                }

                deepExtendConcat(this.blueprint.modules[moduleName], extension);
            }
        }
    }
}
